/* eslint-disable no-console */
/**
 * Phase 4.5 of RAILGUN_HYBRID_REWRITE_PLAN.md — security telemetry channel.
 *
 * The Phase 4.1 / 4.2 / (future 4.3) verifiers detect three classes of
 * silent-failure-grade events that absolutely must page someone, not just land
 * in a bug tracker as "user reported balance off":
 *
 *   - `merkleRoot mismatch` — the on-chain `rootHistory` says our local UTXO
 *      tree is wrong. Either a malicious RPC fed us bad logs, or the local
 *      cache got corrupted. Spends are blocked downstream; we still want a
 *      paged alert because this is the canary for "RPC trust compromised."
 *   - `nullifier mismatch` — the user's spendable note set contains nullifiers
 *      that already appear on-chain (from a Phase 4.2 quorum scan). Means
 *      cache is stale AND the spend flow almost issued a double-spend that
 *      would have been rejected by the contract.
 *   - `cache HMAC fail` — Phase 4.3, future. Persisted cache integrity check
 *      failed; in-process tampering or storage corruption.
 *
 * This module is the single funnel for all three signals. It dual-pipes:
 *  1. `console.error` with a stable `[RailgunSecurity]` prefix so the event is
 *     greppable in logs even when Sentry is disabled (privacy-conscious users).
 *  2. `Sentry.captureMessage` with a `level: 'error'` and a `railgun-security`
 *     tag so the alert routing rule can page on it without filtering out
 *     ordinary errors.
 *
 * Sentry is dynamically imported and best-effort: a missing DSN, a disabled
 * crash-analytics setting, or a Sentry import failure all degrade silently to
 * console-only. The point is to never lose the signal in console; Sentry is
 * the bonus paging path.
 */
export type RailgunSecurityEventKind =
  | 'merkleRootMismatch'
  | 'merkleRootQuorumDisagreement'
  | 'nullifierMismatch'
  | 'nullifierQuorumDisagreement'
  | 'cacheHmacFail'

type EventPayload = Record<string, string | number | boolean | null>

const PREFIX = '[RailgunSecurity]'
const SENTRY_TAG = 'railgun-security'

let sentryModule: typeof import('@sentry/browser') | null = null
let sentryLoadAttempted = false
async function tryGetSentry(): Promise<typeof import('@sentry/browser') | null> {
  if (sentryLoadAttempted) return sentryModule
  sentryLoadAttempted = true
  try {
    sentryModule = (await import('@sentry/browser')) as unknown as typeof import('@sentry/browser')
  } catch {
    sentryModule = null
  }
  return sentryModule
}

export function reportRailgunSecurityEvent(
  kind: RailgunSecurityEventKind,
  payload: EventPayload = {}
): void {
  // 1. Console — always. Stable prefix so log-grep + Sentry breadcrumbs both pick it up.
  console.error(`${PREFIX} ${kind}`, payload)

  // 2. Sentry — best-effort. Tagged so paging rules can match precisely.
  void (async () => {
    const Sentry = await tryGetSentry()
    if (!Sentry) return
    try {
      Sentry.withScope((scope) => {
        scope.setTag(SENTRY_TAG, kind)
        scope.setLevel('error')
        for (const [k, v] of Object.entries(payload)) scope.setExtra(k, v)
        Sentry.captureMessage(`Railgun security event: ${kind}`)
      })
    } catch {
      // ignore — we already logged to console
    }
  })()
}
